import type { LanguageCode } from "./i18n";
import type { BillingPlan, BillingPlanCode } from "./billing.types";

type PlanCopy = {
  name: string;
  description: string;
  features: string[];
  badge?: string;
};

export const billingPlanCopy: Record<LanguageCode, Record<BillingPlanCode, PlanCopy>> = {
  de: {
    contractor_directory: {
      name: "Handwerkerverzeichnis",
      description:
        "Für Auftragnehmer, die mit einem professionellen Profil gefunden werden möchten.",
      features: [
        "Auffindbares Unternehmensprofil im Handwerkerverzeichnis",
        "Leistungen, Regionen und Kontaktmöglichkeiten überzeugend präsentieren",
        "Vertrauen mit einem vollständigen, geprüften Auftritt aufbauen",
        "Firmenbeschreibung und Spezialisierungen sichtbar machen",
        "Direkte Kontaktaufnahme durch interessierte Kunden ermöglichen",
        "Schweizweite Sichtbarkeit rund um die Uhr",
        "Keine Provision auf vermittelte Aufträge",
        "Monatlich pausierbar – volle Kostenkontrolle",
      ],
    },
    contractor_pro: {
      name: "Auftragnehmer Pro",
      description:
        "Für Betriebe, die kontinuierlich passende Aufträge gewinnen und direkt mit Kunden in Kontakt treten möchten.",
      features: [
        "Alles aus dem Handwerkerverzeichnis-Paket",
        "Zugriff auf geprüfte, passende Aufträge in Ihrer Region",
        "Innerhalb von 72 Stunden Interesse an passenden Aufträgen bekunden",
        "Neue Aufträge nach Kategorie und Ort durchsuchen",
        "Projektumfang, Zeitraum und Budget vorab einsehen",
        "Ohne Einzelgebühr auf beliebig viele passende Aufträge reagieren",
        "Kontaktdaten erst nach Auswahl durch den Auftraggeber erhalten",
        "Keine Erfolgsprovision – eine klare Pauschale, egal wie viele Aufträge Sie gewinnen",
      ],
      badge: "Empfohlen",
    },
    agent_membership: {
      name: "Makler-Mitgliedschaft",
      description: "Das professionelle Begner-Profil für Immobilienmakler.",
      features: [
        "Öffentliches, professionelles Maklerprofil",
        "Unternehmen, Leistungen und Regionen präsentieren",
        "Kontaktmöglichkeiten übersichtlich darstellen",
        "Persönliche Prüfung vor der Veröffentlichung",
        "Vertrauen mit einem vollständigen Firmenauftritt stärken",
        "Schweizweite Sichtbarkeit für Eigentümer und Interessenten",
        "Keine Erfolgsprovision auf vermittelte Kontakte",
        "Immobilieninserate separat buchbar",
      ],
    },
    property_14_days: {
      name: "Inserat 14 Tage",
      description: "Ein einzelnes Immobilieninserat mit fester Laufzeit.",
      features: [
        "14 Tage ab Veröffentlichung",
        "Objektdaten, Ausstattung und mehrere Bilder",
        "Kauf- oder Mietangebot klar kennzeichnen",
        "Preis, Verfügbarkeit und Eckdaten darstellen",
        "Genaue Adresse auf Wunsch verborgen halten",
        "Prüfung vor der Veröffentlichung",
        "Laufzeit startet erst mit der Freischaltung",
      ],
    },
    property_30_days: {
      name: "Inserat 30 Tage",
      description: "Mehr Sichtbarkeit für ein einzelnes Immobilienangebot.",
      features: [
        "30 Tage ab Veröffentlichung",
        "Objektdaten, Ausstattung und mehrere Bilder",
        "Kauf- oder Mietangebot klar kennzeichnen",
        "Preis, Verfügbarkeit und Eckdaten darstellen",
        "Genaue Adresse auf Wunsch verborgen halten",
        "Prüfung vor der Veröffentlichung",
        "Mehr Zeit, um passende Interessenten zu erreichen",
      ],
      badge: "Mehr Laufzeit",
    },
  },
  fr: {
    contractor_directory: {
      name: "Annuaire",
      description:
        "Pour les prestataires qui souhaitent être trouvés avec un profil professionnel.",
      features: [
        "Profil d’entreprise visible dans l’annuaire",
        "Présentation des services, régions et coordonnées",
        "Description de l’entreprise et spécialisations",
        "Prise de contact directe par les clients intéressés",
        "Présence dans toute la Suisse, 24 h sur 24",
        "Profil vérifié pour renforcer la confiance",
        "Aucune commission sur les mandats obtenus",
        "Suspension mensuelle pour garder le contrôle des coûts",
      ],
    },
    contractor_pro: {
      name: "Prestataire Pro",
      description:
        "Pour les entreprises qui souhaitent trouver des mandats et contacter les clients.",
      features: [
        "Tout le contenu de l’offre Annuaire",
        "Accès aux mandats vérifiés de votre région",
        "Manifester votre intérêt pendant les 72 heures de collecte",
        "Recherche par catégorie et par lieu",
        "Aperçu du projet, du délai et du budget",
        "Réponses sans frais individuels supplémentaires",
        "Coordonnées accessibles uniquement après sélection par le client",
        "Aucune commission sur les mandats remportés",
      ],
      badge: "Recommandé",
    },
    agent_membership: {
      name: "Abonnement courtier",
      description: "Le profil Begner professionnel pour les courtiers immobiliers.",
      features: [
        "Profil public et professionnel de courtier",
        "Présentation de l’entreprise, des services et des régions",
        "Coordonnées clairement affichées",
        "Vérification personnelle avant publication",
        "Présence complète pour renforcer la confiance",
        "Visibilité nationale auprès des propriétaires et prospects",
        "Aucune commission sur les contacts générés",
        "Annonces immobilières réservables séparément",
      ],
    },
    property_14_days: {
      name: "Annonce 14 jours",
      description: "Une annonce immobilière individuelle à durée fixe.",
      features: [
        "14 jours dès la publication",
        "Données, équipements et plusieurs photos",
        "Offre de vente ou de location clairement indiquée",
        "Prix, disponibilité et caractéristiques",
        "Adresse exacte masquée sur demande",
        "Vérification avant publication",
        "La durée commence uniquement à l’activation",
      ],
    },
    property_30_days: {
      name: "Annonce 30 jours",
      description: "Davantage de visibilité pour une offre immobilière.",
      features: [
        "30 jours dès la publication",
        "Données, équipements et plusieurs photos",
        "Offre de vente ou de location clairement indiquée",
        "Prix, disponibilité et caractéristiques",
        "Adresse exacte masquée sur demande",
        "Vérification avant publication",
        "Plus de temps pour atteindre les bons prospects",
      ],
      badge: "Plus de visibilité",
    },
  },
  it: {
    contractor_directory: {
      name: "Elenco",
      description: "Per le imprese che desiderano farsi trovare con un profilo professionale.",
      features: [
        "Profilo aziendale visibile nell’elenco",
        "Presentazione di servizi, regioni e contatti",
        "Descrizione dell’azienda e specializzazioni",
        "Contatto diretto da parte dei clienti interessati",
        "Visibilità in tutta la Svizzera, 24 ore su 24",
        "Profilo verificato per rafforzare la fiducia",
        "Nessuna commissione sugli incarichi acquisiti",
        "Sospensione mensile per il pieno controllo dei costi",
      ],
    },
    contractor_pro: {
      name: "Impresa Pro",
      description: "Per le imprese che vogliono trovare incarichi e contattare i clienti.",
      features: [
        "Tutto ciò che offre il pacchetto Elenco",
        "Accesso agli incarichi verificati nella propria regione",
        "Manifestare interesse entro le 72 ore di raccolta",
        "Ricerca per categoria e località",
        "Visione anticipata di progetto, tempistica e budget",
        "Risposte senza costi individuali aggiuntivi",
        "Contatti disponibili solo dopo la selezione da parte del cliente",
        "Nessuna commissione sugli incarichi acquisiti",
      ],
      badge: "Consigliato",
    },
    agent_membership: {
      name: "Abbonamento agente",
      description: "Il profilo Begner professionale per agenti immobiliari.",
      features: [
        "Profilo pubblico e professionale dell’agente",
        "Presentazione di azienda, servizi e regioni",
        "Contatti presentati in modo chiaro",
        "Verifica personale prima della pubblicazione",
        "Profilo completo per rafforzare la fiducia",
        "Visibilità nazionale per proprietari e interessati",
        "Nessuna commissione sui contatti generati",
        "Annunci immobiliari acquistabili separatamente",
      ],
    },
    property_14_days: {
      name: "Annuncio 14 giorni",
      description: "Un singolo annuncio immobiliare con durata fissa.",
      features: [
        "14 giorni dalla pubblicazione",
        "Dati, dotazioni e più immagini",
        "Offerta di vendita o affitto chiaramente indicata",
        "Prezzo, disponibilità e caratteristiche",
        "Indirizzo esatto nascosto su richiesta",
        "Verifica prima della pubblicazione",
        "La durata inizia solo con l’attivazione",
      ],
    },
    property_30_days: {
      name: "Annuncio 30 giorni",
      description: "Maggiore visibilità per una singola offerta immobiliare.",
      features: [
        "30 giorni dalla pubblicazione",
        "Dati, dotazioni e più immagini",
        "Offerta di vendita o affitto chiaramente indicata",
        "Prezzo, disponibilità e caratteristiche",
        "Indirizzo esatto nascosto su richiesta",
        "Verifica prima della pubblicazione",
        "Più tempo per raggiungere gli interessati giusti",
      ],
      badge: "Più visibilità",
    },
  },
  en: {
    contractor_directory: {
      name: "Directory",
      description: "For contractors who want to be found through a professional profile.",
      features: [
        "Discoverable company profile in the directory",
        "Show services, regions and contact options",
        "Highlight your company and specialisations",
        "Let interested customers contact you directly",
        "Visibility across Switzerland around the clock",
        "Verified profile to strengthen customer confidence",
        "No commission on jobs you win",
        "Pause monthly for full cost control",
      ],
    },
    contractor_pro: {
      name: "Contractor Pro",
      description: "For businesses that want to find new jobs and contact customers directly.",
      features: [
        "Everything in the Directory plan",
        "Access to verified jobs in your region",
        "Express interest within the 72-hour collection period",
        "Search new jobs by category and location",
        "Review scope, timing and budget in advance",
        "Respond without individual contact fees",
        "Receive contact details only when selected by the customer",
        "No commission on jobs you win",
      ],
      badge: "Recommended",
    },
    agent_membership: {
      name: "Agent membership",
      description: "The professional Begner profile for real estate agents.",
      features: [
        "Public, professional real-estate agent profile",
        "Show company, services and regions",
        "Present contact options clearly",
        "Personal review before publication",
        "Build trust with a complete company presence",
        "Nationwide visibility to owners and prospects",
        "No commission on generated contacts",
        "Property listings booked separately",
      ],
    },
    property_14_days: {
      name: "14-day listing",
      description: "One property listing with a fixed publication period.",
      features: [
        "14 days from publication",
        "Property details, amenities and multiple images",
        "Clearly identify sale or rental offers",
        "Show price, availability and key facts",
        "Keep the exact address private if preferred",
        "Reviewed before publication",
        "The term starts only when the listing goes live",
      ],
    },
    property_30_days: {
      name: "30-day listing",
      description: "More visibility for one property offer.",
      features: [
        "30 days from publication",
        "Property details, amenities and multiple images",
        "Clearly identify sale or rental offers",
        "Show price, availability and key facts",
        "Keep the exact address private if preferred",
        "Reviewed before publication",
        "More time to reach suitable prospects",
      ],
      badge: "More visibility",
    },
  },
};

export const fallbackBillingPlans: BillingPlan[] = [
  {
    code: "contractor_directory",
    productType: "membership",
    eligibleRole: "auftragnehmer",
    priceMinor: 1450,
    regularPriceMinor: 2900,
    currency: "CHF",
    billingInterval: "month",
    periodMonths: 1,
    durationDays: null,
    directoryAccess: true,
    jobContactAccess: false,
  },
  {
    code: "contractor_pro",
    productType: "membership",
    eligibleRole: "auftragnehmer",
    priceMinor: 4950,
    regularPriceMinor: 9900,
    currency: "CHF",
    billingInterval: "month",
    periodMonths: 1,
    durationDays: null,
    directoryAccess: true,
    jobContactAccess: true,
  },
  {
    code: "agent_membership",
    productType: "membership",
    eligibleRole: "immobilienmakler",
    priceMinor: 2950,
    regularPriceMinor: 5900,
    currency: "CHF",
    billingInterval: "month",
    periodMonths: 1,
    durationDays: null,
    directoryAccess: true,
    jobContactAccess: false,
  },
  {
    code: "property_14_days",
    productType: "property_listing",
    eligibleRole: null,
    priceMinor: 8450,
    regularPriceMinor: 16900,
    currency: "CHF",
    billingInterval: "one_time",
    periodMonths: null,
    durationDays: 14,
    directoryAccess: false,
    jobContactAccess: false,
  },
  {
    code: "property_30_days",
    productType: "property_listing",
    eligibleRole: null,
    priceMinor: 14950,
    regularPriceMinor: 29900,
    currency: "CHF",
    billingInterval: "one_time",
    periodMonths: null,
    durationDays: 30,
    directoryAccess: false,
    jobContactAccess: false,
  },
];

export function billingPlanName(code: BillingPlanCode, language: LanguageCode) {
  return billingPlanCopy[language][code].name;
}
